import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { App, Button, Card, Space, Typography, Tag, Alert, Form, Select, Table } from 'antd';
import { trpc } from '../../lib/api';
import { useBreadcrumbTitle } from '../../hooks/useBreadcrumb';
import { useAuthStore } from '../../stores/authStore';
import { usePartnerAuthStore } from '../../stores/partnerAuthStore';
import { formatDateWithFallback } from '../../utils/dateFormat';

const { Title, Text } = Typography;

const PARTNER_SCOPE_LABELS: Record<string, string> = {
  'companies:read': '会社情報の閲覧',
  'locations:read': 'エリアの閲覧',
  'locations:write': 'エリアの更新',
  'equipments:read': '設備の閲覧',
  'equipments:write': '設備の更新',
  'documents:read': '書類の閲覧',
  'documents:write': '書類の更新',
  'files:upload': '書類のアップロード',
  'files:share': 'ファイル共有',
  'reports:read': '報告書の閲覧',
  'reports:write': '報告書の作成・提出',
  'users:manage': '外部ユーザー管理',
};

type PartnerLocationSummary = {
  id: string;
  name: string;
  displayId?: string | null;
};

type PartnerEquipmentSummary = {
  id: string;
  name: string;
  displayId?: string | null;
};

const getScopeResources = (scopes: unknown): string[] => {
  if (!scopes || typeof scopes !== 'object') {
    return [];
  }
  const record = scopes as Record<string, unknown>;
  if (!Array.isArray(record.resources)) {
    return [];
  }
  return record.resources.filter((entry): entry is string => typeof entry === 'string');
};

export function PartnerCompanyDetailPage() {
  const { companyId } = useParams();
  const { message } = App.useApp();
  const { partnerUser } = usePartnerAuthStore();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [form] = Form.useForm<{ assignedManagerUserId?: string }>();
  const appWindowRef = useRef<Window | null>(null);
  const utils = trpc.useUtils();

  const { data: accessData } = trpc.partnerPortal.listAccessibleCompanies.useQuery(
    { status: 'active', page: 1, limit: 100 },
    { enabled: !!companyId }
  );
  const {
    data: partnerUsersData,
    error: partnerUsersError,
    isLoading: partnerUsersLoading,
  } = trpc.partnerAdmin.partnerListUsers.useQuery(
    { page: 1, limit: 100 },
    { retry: false }
  );

  const access = useMemo(() => {
    return accessData?.access.find((row) => row.companyId === companyId) ?? null;
  }, [accessData?.access, companyId]);

  useBreadcrumbTitle(
    'partner-company',
    access?.company?.name ?? access?.companyId ?? null
  );

  const partnerUserMap = useMemo(() => {
    const map = new Map<string, { name: string; email: string; isActive: boolean }>();
    (partnerUsersData?.users ?? []).forEach((user) => {
      map.set(user.id, { name: user.name, email: user.email, isActive: user.isActive });
    });
    return map;
  }, [partnerUsersData?.users]);

  useEffect(() => {
    if (!access) {
      return;
    }
    form.setFieldsValue({
      assignedManagerUserId: access.assignedManagerUserId ?? undefined,
    });
  }, [access, form]);

  const updateAssignedManager = trpc.partnerPortal.updateAssignedManager.useMutation({
    onSuccess: () => {
      message.success('担当者を更新しました');
      void utils.partnerPortal.listAccessibleCompanies.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '担当者の更新に失敗しました');
    },
  });

  const loginToCompanyApp = trpc.partnerPortal.loginToCompanyApp.useMutation({
    onSuccess: (result) => {
      setAuth(result.user, result.tokens.accessToken, result.tokens.refreshToken);
      void utils.partnerPortal.listCompanyLoginLogs.invalidate();
      message.success('担当会社としてログインしました');
      if (appWindowRef.current && !appWindowRef.current.closed) {
        appWindowRef.current.location.href = '/app';
      } else {
        window.open('/app', '_blank');
      }
      appWindowRef.current = null;
    },
    onError: (error) => {
      message.error(error.message || 'ログインに失敗しました');
      if (appWindowRef.current && !appWindowRef.current.closed) {
        appWindowRef.current.close();
      }
      appWindowRef.current = null;
    },
  });

  const {
    data: loginLogsData,
    isLoading: loginLogsLoading,
  } = trpc.partnerPortal.listCompanyLoginLogs.useQuery(
    { companyId: companyId ?? '', limit: 20 },
    { enabled: !!companyId && partnerUser?.role === 'OrgAdmin' }
  );

  const { data: locations, error: locationsError } = trpc.partnerPortal.listLocations.useQuery(
    { companyId: companyId ?? '', branchId: undefined },
    { enabled: !!companyId }
  );

  const { data: equipments, error: equipmentsError } = trpc.partnerPortal.listEquipments.useQuery(
    { companyId: companyId ?? '', page: 1, limit: 20 },
    { enabled: !!companyId }
  );
  const equipmentList = useMemo<PartnerEquipmentSummary[]>(() => {
    const raw = equipments as { items?: PartnerEquipmentSummary[]; data?: PartnerEquipmentSummary[] } | null;
    const list = raw?.items ?? raw?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, [equipments]);

  if (!companyId) {
    return <Alert type="error" message="会社IDが見つかりません" />;
  }

  if (!access) {
    return <Alert type="warning" message="担当会社が見つかりません" />;
  }

  const resources = getScopeResources(access.scopes);
  const scopeLabelText = resources.length > 0
    ? resources.map((value: string) => PARTNER_SCOPE_LABELS[value] ?? value).join(' / ')
    : '未設定';

  const handleAssignedManagerSave = () => {
    if (!companyId) {
      message.error('会社IDが見つかりません');
      return;
    }
    form.validateFields().then((values) => {
      updateAssignedManager.mutate({
        companyId,
        assignedManagerUserId: values.assignedManagerUserId ?? null,
      });
    });
  };

  const loginLogs = (loginLogsData?.logs ?? []).map((log) => {
    const metadata = (log.metadata ?? {}) as Record<string, unknown>;
    const targetName = metadata.targetUserName as string | undefined;
    const targetEmail = metadata.targetUserEmail as string | undefined;
    const targetUser = targetName
      ? `${targetName}${targetEmail ? ` (${targetEmail})` : ''}`
      : targetEmail || (metadata.targetUserId as string | undefined) || '-';
    const actor = log.partnerUserName || log.partnerUserEmail || log.partnerUserId || '-';
    return {
      id: log.id,
      createdAt: log.createdAt,
      actor,
      targetUser,
    };
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Space direction="vertical" size={2}>
          <Title level={3} style={{ marginBottom: 0 }}>
            {access.company?.name ?? access.companyId}
          </Title>
          <Text type="secondary">{access.companyId}</Text>
          {partnerUser?.role === 'OrgAdmin' && (
            <Button
              type="primary"
              onClick={() => {
                if (!companyId) {
                  message.error('会社IDが見つかりません');
                  return;
                }
                if (!appWindowRef.current || appWindowRef.current.closed) {
                  appWindowRef.current = window.open('about:blank', '_blank');
                }
                loginToCompanyApp.mutate({ companyId });
              }}
              loading={loginToCompanyApp.isPending}
            >
              アプリへログイン
            </Button>
          )}
        </Space>
      </div>

      <Card title="アクセス情報">
        <Space direction="vertical" size="small">
          <Text>ステータス: <Tag color={access.status === 'active' ? 'green' : 'default'}>{access.status}</Tag></Text>
          <Text>ロール: {access.accessRole ?? '-'}</Text>
          <Text>
            担当者: {access.assignedManagerUserId
              ? (() => {
                const user = partnerUserMap.get(access.assignedManagerUserId);
                if (!user) {
                  return '不明';
                }
                return `${user.name}${user.isActive ? '' : '（招待中）'}`;
              })()
              : '-'}
          </Text>
          <Text>権限: {scopeLabelText}</Text>
        </Space>
      </Card>

      <Card title="担当者（代理店側）">
        <Form form={form} layout="vertical">
          <Form.Item name="assignedManagerUserId" label="担当者">
            <Select
              allowClear
              placeholder="担当者を選択（任意）"
              options={(partnerUsersData?.users ?? []).map((user) => ({
                value: user.id,
                label: `${user.name} (${user.email})${user.isActive ? '' : '（招待中）'}`,
                disabled: !user.isActive,
              }))}
              showSearch
              optionFilterProp="label"
              loading={partnerUsersLoading}
            />
          </Form.Item>
          {partnerUsersError && (
            <Text type="secondary">
              担当者一覧を取得できません。代理店管理者でログインしているか確認してください。
            </Text>
          )}
          <Button
            type="primary"
            onClick={handleAssignedManagerSave}
            loading={updateAssignedManager.isPending}
          >
            更新する
          </Button>
        </Form>
      </Card>

      {partnerUser?.role === 'OrgAdmin' && (
        <Card title="ログイン履歴">
          <Table
            dataSource={loginLogs}
            loading={loginLogsLoading}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: '日時',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 200,
                render: (value) => formatDateWithFallback(value, { showTime: true }),
              },
              {
                title: '実行者',
                dataIndex: 'actor',
                key: 'actor',
              },
              {
                title: 'ログイン先ユーザー',
                dataIndex: 'targetUser',
                key: 'targetUser',
              },
            ]}
          />
        </Card>
      )}

      <Card title="物件一覧">
        {locationsError ? (
          <Alert type="warning" message="物件一覧の取得権限がありません" />
        ) : (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {(locations ?? []).length === 0 ? (
              <Text type="secondary">物件がありません。</Text>
            ) : (
              ((locations ?? []) as PartnerLocationSummary[]).map((location) => (
                <div key={location.id}>
                  <Text strong>{location.name}</Text>
                  <div style={{ fontSize: 12, color: '#888' }}>{location.displayId ?? location.id}</div>
                </div>
              ))
            )}
          </Space>
        )}
      </Card>

      <Card title="設備一覧（最新20件）">
        {equipmentsError ? (
          <Alert type="warning" message="設備一覧の取得権限がありません" />
        ) : (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {equipmentList.length === 0 ? (
              <Text type="secondary">設備がありません。</Text>
            ) : (
              (equipmentList as PartnerEquipmentSummary[]).map((equipment) => (
                <div key={equipment.id}>
                  <Text strong>{equipment.name}</Text>
                  <div style={{ fontSize: 12, color: '#888' }}>{equipment.displayId ?? equipment.id}</div>
                </div>
              ))
            )}
          </Space>
        )}
      </Card>
    </Space>
  );
}
